import React, { useState, useEffect } from "react";
import PetPanel from "./modules/mascotas/PetPanel";
import SponsorPanel from "./modules/patrocinadores/SponsorPanel";
import AdminQRPage from "./QRCode/QRCode.jsx";
import Login from "./modules/auth/Login";
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  Container,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PetsIcon from "@mui/icons-material/Pets";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import LogoutIcon from "@mui/icons-material/Logout";
import QrCodeIcon from "@mui/icons-material/QrCode";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { setAuthToken } from "./api/auth";
import { registerUnauthorizedHandler } from "./api/client";

const appVersion = process.env.REACT_APP_VERSION || "desconocida";

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const tokenTime = localStorage.getItem("token_time");
    if (token && tokenTime && Date.now() - tokenTime < 1000 * 60 * 60 * 5) {
      setAuthToken(token);
      setAuthenticated(true);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("token_time");
      setAuthToken(null);
      setAuthenticated(false);
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("token_time");
      setAuthToken(null);
      setAuthenticated(false);
      setMobileOpen(false);
      navigate("/login", { replace: true });
    });

    return () => {
      registerUnauthorizedHandler(null);
    };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("token_time");
    setAuthToken(null);
    setAuthenticated(false);
    setMobileOpen(false);
    navigate("/login", { replace: true });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePanelNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navItems = [
    { label: "Mascotas", path: "/pets", icon: PetsIcon },
    {
      label: "Patrocinadores",
      path: "/sponsors",
      icon: VolunteerActivismIcon,
    },
    {
      label: "Descargar QR",
      path: "/qr",
      icon: QrCodeIcon,
    },
  ];

  const isActiveRoute = (path) => location.pathname === path;

  if (!authChecked) return null;

  const drawer = (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        variant="h6"
        sx={{ my: 2, fontFamily: "'Comic Relief', cursive" }}
      >
        Menú
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton onClick={() => handlePanelNavigation(item.path)}>
              <item.icon sx={{ mr: 1 }} />
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} />
            <ListItemText primary="Salir" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Routes>
      <Route
        path="/login"
        element={
          authenticated ? (
            <Navigate to="/pets" replace />
          ) : (
            <Login onLogin={() => setAuthenticated(true)} />
          )
        }
      />
      <Route
        path="*"
        element={
          authenticated ? (
            <Box
              className="main-background"
              sx={{
                fontFamily: "'Alexandria', sans-serif",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <AppBar
                position="static"
                sx={{ bgcolor: theme.palette.primary.main }}
              >
                <Toolbar>
                  <IconButton
                    color="inherit"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ display: { md: "none" }, mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>

                  <Typography
                    variant="h4"
                    sx={{
                      flexGrow: 1,
                      fontFamily: "'Comic Relief', cursive",
                      color: "#fff",
                    }}
                  >
                    Administrador
                  </Typography>

                  <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
                    {navItems.map((item) => (
                      <Button
                        key={item.path}
                        color="inherit"
                        onClick={() => handlePanelNavigation(item.path)}
                        startIcon={<item.icon />}
                        sx={{
                          bgcolor: isActiveRoute(item.path)
                            ? "#20ba59"
                            : "transparent",
                          "&:hover": { bgcolor: "#20ba59" },
                          fontWeight: isActiveRoute(item.path) ? "bold" : 500,
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                    <Button
                      color="inherit"
                      onClick={handleLogout}
                      startIcon={<LogoutIcon />}
                      sx={{
                        "&:hover": { bgcolor: "#FFA500", color: "#0D0D0D" },
                        fontWeight: "bold",
                        ml: 1,
                      }}
                    >
                      Salir
                    </Button>
                  </Box>
                </Toolbar>
              </AppBar>

              <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                  display: { md: "none" },
                  "& .MuiDrawer-paper": {
                    boxSizing: "border-box",
                    width: 250,
                  },
                }}
              >
                {drawer}
              </Drawer>

              <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
                <Routes>
                  <Route path="/" element={<Navigate to="/pets" replace />} />
                  <Route path="/pets" element={<PetPanel />} />
                  <Route path="/sponsors" element={<SponsorPanel />} />
                  <Route path="/qr" element={<AdminQRPage />} />
                  <Route path="*" element={<Navigate to="/pets" replace />} />
                </Routes>
              </Container>

              <Box
                component="footer"
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: "1px solid rgba(27, 141, 75, 0.16)",
                  backgroundColor: "#f6fbf8",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "#456",
                    fontFamily: "'Alexandria', sans-serif",
                  }}
                >
                  Version del codigo: {appVersion}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

export default App;
